import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  ButtonVariants,
  Divider,
  DropdownItem,
  DropdownList,
  FormControlSizes,
  HStack,
  Icon,
  ICON_SIZES,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Popover,
  Text,
  useDisclosure,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';
import { useCallback, useMemo, useState, type CSSProperties, type MouseEvent } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toNavGroupName } from '../../hooks/useSpacesNavItems';
import { useUser } from '../../hooks/useUser';
import { useSpacesConfig } from '../../providers/AppLayoutConfig/SpaceConfigContext';
import { useAppStateSelector } from '../../providers/AppStateProvider';
import type { NavigationItem } from '../../tokens/navigationPaths';
import type { LayoutSection, LayoutSpec } from '../../types/LayoutConfig';
import { selectLayoutOptions } from '../AppLayout/AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../AppLayout/Layouts/tokens';
import { useLayoutSpecsQuery, type LayoutSpecResponse } from '../AppLayout/LayoutSpecs/LayoutSpecSlice';
import { useSectionSpaceMetadata } from './hooks/useSectionSpaceMetadata';
import { ManageSpaceModal } from './Spaces/ManageSpaceModal/ManageSpaceModal';
import { ManageTemplateModal } from './Spaces/ManageTemplateModal/ManageTemplateModal';
import { RemoveSpaceDialog } from './Spaces/RemoveSpaceDialog';
import { SidebarSubMenuNavItemRowStatic, SidebarSubMenuNavItemWrapper } from './styles';

const TARGET_STYLE: CSSProperties = {
  width: '100%',
  display: 'block',
};

const ICON_BUTTON_STYLE: CSSProperties = {
  margin: '-4px',
};

const DEVICE_TYPE_ICONS = {
  mobile: IconName.DeviceMobile,
  tablet: IconName.DeviceTablet,
  laptop: IconName.DeviceLaptop,
  desktop: IconName.DeviceDesktop,
  widescreen: IconName.DeviceDesktopWide,
  all: IconName.Globe,
};

export function SidebarSubMenuNavItem({
  item,
  layoutSection,
  rearrangeModeActive = false,
  onStartRearranging,
}: {
  item: NavigationItem;
  layoutSection: LayoutSection | undefined;
  /** When true, sidebar is in rearrange mode: rows render as static blocks (not links); editable rows are draggable. */
  rearrangeModeActive?: boolean;
  onStartRearranging?: () => void;
}) {
  const mixpanel = useMixpanel();
  const { updateSpace, removeSpace } = useSpacesConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const newPath = replaceLastPathSegment(item.path, DEFAULT_LAYOUT_ID);
  const match = useMatch(item.path ?? '');
  const isActive = !!match;
  // True when the user is currently viewing this space (including sub-paths like deep-dives)
  const isOnThisSpace =
    item.path != null && (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
  const user = useUser();
  const layoutOptions = useAppStateSelector(selectLayoutOptions);
  const { data: sharedLayoutSpecs } = useLayoutSpecsQuery();

  const isRowDraggable = rearrangeModeActive && !!item.isEditable;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id ?? item.path ?? '',
    disabled: !isRowDraggable,
  });

  const sortableStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide source row while dragging — DragOverlay in NavigationSidebar shows the preview.
    opacity: rearrangeModeActive && isDragging ? 0 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };

  // Find the template config if this space was created from an editable template
  const editableTemplateConfig = useMemo((): LayoutSpecResponse | undefined => {
    if (!item.id || !layoutOptions || !sharedLayoutSpecs) {
      return undefined;
    }
    const layoutSpec = layoutOptions[item.id];
    if (!layoutSpec?.templateId) {
      return undefined;
    }
    // Find the template in the shared specs that matches the templateId and is owned by the current user
    return sharedLayoutSpecs.find(spec => spec.Config.id === layoutSpec.templateId && spec.User === user.Name);
  }, [item.id, layoutOptions, sharedLayoutSpecs, user.Name]);

  const canEditSpace = item.id != null && layoutOptions?.[item.id] != null;
  const canRemoveSpace = item.id != null && item.id !== DEFAULT_LAYOUT_ID && layoutOptions?.[item.id] != null;

  const popover = usePopoverState({
    placement: 'right-start',
    trigger: '',
    usePortal: true,
    noPaddingAndBorder: true,
    closeOnClickOutside: true,
  });

  const manageSpaceDialog = useDisclosure();
  const removeDialog = useDisclosure();
  const manageTemplateDialog = useDisclosure();
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);

  const resolvedLayoutSection = layoutSection ?? 'trading';
  const { existingGroups, sectionLayoutIdsAndLabels } = useSectionSpaceMetadata(resolvedLayoutSection);

  const handleOpenDropdown = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    popover.open();
  };

  const handleOpenCreateTemplateDialog = useCallback(() => {
    popover.close();
    setIsEditingTemplate(false);
    mixpanel.track(MixpanelEvent.OpenCreateTemplateModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebarSubNavItem,
    });
    manageTemplateDialog.open();
  }, [popover, manageTemplateDialog, mixpanel, layoutSection]);

  const handleOpenEditTemplateDialog = useCallback(() => {
    popover.close();
    mixpanel.track(MixpanelEvent.OpenEditTemplateModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebarSubNavItem,
    });
    setIsEditingTemplate(true);
    manageTemplateDialog.open();
  }, [popover, manageTemplateDialog, mixpanel, layoutSection]);

  const handleOpenEditSpaceDialog = useCallback(() => {
    popover.close();
    mixpanel.track(MixpanelEvent.OpenEditSpaceModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebarSubNavItem,
    });
    manageSpaceDialog.open();
  }, [popover, manageSpaceDialog, mixpanel, layoutSection]);

  const handleUpdateSpace = useCallback(
    (id: string, config: Partial<LayoutSpec>) => {
      updateSpace(id, config);
      mixpanel.track(MixpanelEvent.UpdateSpace, {
        [MixpanelEventProperty.LayoutSection]: layoutSection,
        [MixpanelEventProperty.Group]: config.group,
        [MixpanelEventProperty.DeviceType]: config.deviceType,
      });

      // If the group changed and the user is currently on this space, navigate to the
      // new canonical path so the URL reflects the updated group segment.
      if (isOnThisSpace && item.id && layoutSection && 'group' in config) {
        const newGroup = config.group;
        const newSpacePath = newGroup
          ? `/${layoutSection}/${toNavGroupName(newGroup)}/${item.id}`
          : `/${layoutSection}/${item.id}`;
        if (!location.pathname.startsWith(newSpacePath)) {
          navigate(newSpacePath);
        }
      }
    },
    [updateSpace, mixpanel, layoutSection, isOnThisSpace, item.id, location.pathname, navigate]
  );

  const handleRemove = useCallback(() => {
    if (!item.id) {
      return;
    }
    removeSpace(item.id);
    mixpanel.track(MixpanelEvent.RemoveSpace, {
      [MixpanelEventProperty.LayoutSection]: resolvedLayoutSection,
      [MixpanelEventProperty.DeviceType]: item.deviceType ?? 'all',
    });
    if (isActive && newPath) {
      navigate(newPath);
    }
  }, [item.id, removeSpace, mixpanel, resolvedLayoutSection, item.deviceType, isActive, newPath, navigate]);

  const handleRearrange = useCallback(() => {
    popover.close();
    onStartRearranging?.();
  }, [popover, onStartRearranging]);

  const rowInner = (
    <>
      <Box flex="1 1 0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {item.label}
      </Box>
      <Actions>
        {item.deviceType && item.deviceType !== 'all' && (
          <Box px="spacing4">
            <Icon icon={DEVICE_TYPE_ICONS[item.deviceType]} />
          </Box>
        )}
        {item.isEditable && isRowDraggable && (
          <DragHandle data-testid="drag-handle" {...attributes} {...listeners}>
            <Icon icon={IconName.DragVertical} color="colorTextImportant" size={ICON_SIZES.MEDIUM} />
          </DragHandle>
        )}
        {item.isEditable && !isRowDraggable && (
          <IconButton
            ghost
            onClick={handleOpenDropdown}
            style={ICON_BUTTON_STYLE}
            icon={IconName.DotsVertical}
            size={FormControlSizes.Small}
          />
        )}
      </Actions>
    </>
  );

  return (
    <div ref={setNodeRef} style={sortableStyle}>
      <Popover {...popover} targetStyle={TARGET_STYLE}>
        {rearrangeModeActive ? (
          <SidebarSubMenuNavItemRowStatic
            key={item.path}
            data-testid="sidebar-submenu-nav-item"
            className={isActive ? 'active' : undefined}
          >
            {rowInner}
          </SidebarSubMenuNavItemRowStatic>
        ) : (
          <SidebarSubMenuNavItemWrapper
            key={item.path}
            data-testid="sidebar-submenu-nav-item"
            to={item.path || ''}
            onClick={() => {
              if (item.mixpanelEvent) {
                mixpanel.track(MixpanelEvent.NavigateTo, {
                  path: item.path,
                  source: MixpanelEventSource.NavigationSidebarSubNavItem,
                });
                mixpanel.track(item.mixpanelEvent);
              }
            }}
          >
            {rowInner}
          </SidebarSubMenuNavItemWrapper>
        )}
        {item.isEditable && !rearrangeModeActive && (
          <EditableItemMenu
            layoutSection={layoutSection}
            editableTemplateConfig={editableTemplateConfig}
            canEditSpace={canEditSpace}
            canRemoveSpace={canRemoveSpace}
            onOpenEditDialog={handleOpenEditSpaceDialog}
            onOpenRemoveDialog={removeDialog.open}
            onOpenCreateTemplateDialog={handleOpenCreateTemplateDialog}
            onOpenEditTemplateDialog={handleOpenEditTemplateDialog}
            onRearrange={handleRearrange}
          />
        )}
      </Popover>
      {layoutSection && item.id && manageTemplateDialog.isOpen && (
        <ManageTemplateModal
          key={`manage_template_${manageTemplateDialog.isOpen.toString()}_${isEditingTemplate.toString()}`}
          layoutSection={layoutSection}
          initialLayoutId={item.id}
          selectedTemplateConfig={isEditingTemplate ? editableTemplateConfig : undefined}
          {...manageTemplateDialog}
        />
      )}
      {layoutSection && manageSpaceDialog.isOpen && item.id && layoutOptions?.[item.id] && (
        <ManageSpaceModal
          layoutSection={layoutSection}
          onEditTemplate={handleOpenEditTemplateDialog}
          onUpdateSpace={handleUpdateSpace}
          initialLayoutSpec={layoutOptions[item.id]}
          existingGroups={existingGroups}
          sectionLayoutIdsAndLabels={sectionLayoutIdsAndLabels}
          {...manageSpaceDialog}
        />
      )}

      <RemoveSpaceDialog
        key={`remove_${removeDialog.isOpen.toString()}`}
        label={item.label}
        onConfirm={handleRemove}
        {...removeDialog}
      />
    </div>
  );
}

interface EditableItemMenuProps {
  layoutSection: LayoutSection | undefined;
  editableTemplateConfig: LayoutSpecResponse | undefined;
  canEditSpace: boolean;
  canRemoveSpace: boolean;
  onOpenEditDialog: () => void;
  onOpenRemoveDialog: () => void;
  onOpenCreateTemplateDialog: () => void;
  onOpenEditTemplateDialog: () => void;
  onRearrange: () => void;
}

function EditableItemMenu({
  layoutSection,
  editableTemplateConfig,
  canEditSpace,
  canRemoveSpace,
  onOpenEditDialog,
  onOpenRemoveDialog,
  onOpenCreateTemplateDialog,
  onOpenEditTemplateDialog,
  onRearrange,
}: EditableItemMenuProps) {
  return (
    <DropdownList>
      {layoutSection && (
        <DropdownItem ghost startIcon={IconName.Share} onClick={onOpenCreateTemplateDialog}>
          <Text>Share</Text>
        </DropdownItem>
      )}
      {editableTemplateConfig && (
        <DropdownItem ghost startIcon={IconName.Template} onClick={onOpenEditTemplateDialog}>
          <Text>Edit Template</Text>
        </DropdownItem>
      )}
      <DropdownItem ghost startIcon={IconName.Pencil} disabled={!canEditSpace} onClick={onOpenEditDialog}>
        <Text>Edit Space</Text>
      </DropdownItem>
      <DropdownItem ghost startIcon={IconName.Reorder} onClick={onRearrange}>
        <Text>Rearrange</Text>
      </DropdownItem>
      <Divider />
      <DropdownItem
        ghost
        startIcon={IconName.Trash}
        disabled={!canRemoveSpace}
        variant={ButtonVariants.Negative}
        onClick={onOpenRemoveDialog}
      >
        <Text>Remove</Text>
      </DropdownItem>
    </DropdownList>
  );
}

function replaceLastPathSegment(path: string | undefined, replacement: string): string | undefined {
  if (!path) {
    return undefined;
  }
  return path.split('/').slice(0, -1).concat(replacement).join('/');
}

const Actions = styled(HStack)`
  gap: ${({ theme }) => theme.spacing4}px;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  margin: -4px;
  padding: 4px;
  color: ${({ theme }) => theme.colorTextSubtle};

  &:active {
    cursor: grabbing;
  }
`;
